import { Slider } from "@components/atoms/Slider"
import { cn } from "@utils/tw"
import { defineBlock, EditableText, InnerBlocks, useInlineEditableValue } from "eddev/blocks"
import { Link } from "eddev/routing"
import { ReactNode } from "react"

export const meta: BlockMeta = {
  title: "Browse Mode Quicklinks",
  category: "layouts",
  icon: "slides",
  description: "A row/grid of quick-links to different browse modes",
  keywords: ["category", "genre", "tile", "browse"],
  tags: ["#featured"],
  flags: {
    fullWidth: true,
    marginRule: "featured-card",
  },
}

const DEFAULT_CARDS: CardProps[] = [
  {
    id: "a-z",
    label: "A-Z",
    description: "Browse every film and event in this year's program",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256">
        <path d="M216,44H40A12,12,0,0,0,28,56V200a12,12,0,0,0,12,12H216a12,12,0,0,0,12-12V56A12,12,0,0,0,216,44ZM36,84h88v88H36Zm96-8V52h40V76Zm-8,0H84V52h40Zm0,104v24H84V180Zm8,0h40v24H132Zm0-8V84h88v88ZM220,56V76H180V52h36A4,4,0,0,1,220,56ZM40,52H76V76H36V56A4,4,0,0,1,40,52ZM36,200V180H76v24H40A4,4,0,0,1,36,200Zm180,4H180V180h40v20A4,4,0,0,1,216,204Z"></path>
      </svg>
    ),
    href: "/program/a-z",
  },
  {
    id: "by-date",
    label: "By Date",
    description: "Browse the full program by date and time",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256">
        <path d="M232,100a4,4,0,0,0,4-4V64a12,12,0,0,0-12-12H32A12,12,0,0,0,20,64V96a4,4,0,0,0,4,4,28,28,0,0,1,0,56,4,4,0,0,0-4,4v32a12,12,0,0,0,12,12H224a12,12,0,0,0,12-12V160a4,4,0,0,0-4-4,28,28,0,0,1,0-56ZM28,192V163.78a36,36,0,0,0,0-71.56V64a4,4,0,0,1,4-4H92V196H32A4,4,0,0,1,28,192Zm168-64a36.06,36.06,0,0,0,32,35.78V192a4,4,0,0,1-4,4H100V60H224a4,4,0,0,1,4,4V92.22A36.06,36.06,0,0,0,196,128Z"></path>
      </svg>
    ),
    href: "/program/by-date",
  },
  {
    id: "calendar",
    label: "Calendar",
    description: "Dive into the schedule with our interactive calendar",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256">
        <path d="M208,36H180V24a4,4,0,0,0-8,0V36H84V24a4,4,0,0,0-8,0V36H48A12,12,0,0,0,36,48V208a12,12,0,0,0,12,12H208a12,12,0,0,0,12-12V48A12,12,0,0,0,208,36ZM48,44H76V56a4,4,0,0,0,8,0V44h88V56a4,4,0,0,0,8,0V44h28a4,4,0,0,1,4,4V84H44V48A4,4,0,0,1,48,44ZM208,212H48a4,4,0,0,1-4-4V92H212V208A4,4,0,0,1,208,212ZM108,120v64a4,4,0,0,1-8,0V126.47l-10.21,5.11a4,4,0,0,1-3.58-7.16l16-8A4,4,0,0,1,108,120Zm60,28-24,32h24a4,4,0,0,1,0,8H136a4,4,0,0,1-3.2-6.4l28.78-38.37A11.88,11.88,0,0,0,164,136a12,12,0,0,0-22.4-6,4,4,0,0,1-6.92-4A20,20,0,0,1,172,136,19.79,19.79,0,0,1,168,148Z"></path>
      </svg>
    ),
    href: "/program/calendar",
  },
  {
    id: "film-finder",
    label: "Trailer Roulette",
    description: "Watch every trailer and find your next favorite film",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#000000" viewBox="0 0 256 256">
        <path d="M162.22,108.67l-48-32A4,4,0,0,0,108,80v64a4,4,0,0,0,2.11,3.53,4,4,0,0,0,4.11-.2l48-32a4,4,0,0,0,0-6.66ZM116,136.53V87.47L152.79,112ZM216,44H40A12,12,0,0,0,28,56V168a12,12,0,0,0,12,12H216a12,12,0,0,0,12-12V56A12,12,0,0,0,216,44Zm4,124a4,4,0,0,1-4,4H40a4,4,0,0,1-4-4V56a4,4,0,0,1,4-4H216a4,4,0,0,1,4,4Zm8,40a4,4,0,0,1-4,4H32a4,4,0,0,1,0-8H224A4,4,0,0,1,228,208Z"></path>
      </svg>
    ),
    href: "/program/trailers/",
  },
]

export default defineBlock("hero/browse-quicklinks", (props) => {
  const modes = props.modes ?? []

  const heading = useInlineEditableValue("title", "More ways to browse")

  const totalModes = modes.length > 0 ? modes.length : DEFAULT_CARDS.length

  return (
    <div data-mixpanel-block="Browse Mode Quicklinks" data-mixpanel-heading={heading[0]}>
      <div className="w-grid mx-auto text-fg">
        <EditableText
          as="h2"
          store={heading}
          defaultValue="More ways to browse"
          className="type-title-m mb-5"
          plainText
        />
      </div>
      <div className="w-grid mx-auto">
        <div
          className={cn(
            "grid gap-grid-gutter",
            totalModes === 2
              ? "grid-cols-2 sm:grid-cols-2"
              : totalModes === 3
                ? "grid-cols-1 sm:grid-cols-3"
                : "grid-cols-2 md:grid-cols-4",
          )}
        >
          {DEFAULT_CARDS.filter((card) => modes.length === 0 || modes.includes(card.id)).map((card) => (
            <Card key={card.id} {...card} />
          ))}
        </div>
      </div>
    </div>
  )
})

type CardProps = {
  id: string
  label: string
  description: string
  icon: ReactNode
  href: string
}

function Card(props: CardProps) {
  return (
    <Link
      href={props.href}
      draggable={false}
      className="text-fg bg-bg-mid text-balance type-title-l md:type-title-m lg:type-title-l min-h-[5em] p-4 flex flex-col gap-[0.2em] transition-colors duration-400 is-theme-dark:hover:bg-neutral-700 isolate"
      as={env.admin ? "div" : undefined}
    >
      <div className="flex justify-between gap-3">
        <EditableText
          store={"title_" + props.id}
          as="div"
          defaultValue={props.label}
          plainText
          disableLineBreaks
          className="mt-[0.1em] lg:mt-0"
        />
        <div className="el-svg:[fill:currentColor] el-svg:block md:mt-[-0.2em] lg:mt-0">{props.icon}</div>
      </div>
      <div className="flex-grow" />
      <EditableText
        store={"description_" + props.id}
        as="div"
        className="type-ui-m max-w-[18em]"
        defaultValue={props.description}
        plainText
        disableLineBreaks
      />
    </Link>
  )
}
